import { dateTimeFormatter, durationNanosecondsToString, isoToLocalTimeZoneDate } from '@/vantage/dateTime.utilities';
import type { ValueFormatterParams } from 'ag-grid-community';
import _ from 'lodash';
import { AUTO_FORMAT, formatNumber } from '@/utilities/numberHelper.utilities';

export const generateReferenceColumnValueFormatter = (
  column: string,
  t: (path: string | string[]) => string = (path) => (Array.isArray(path) ? path[0] : path),
) => {
  return ({ node }: ValueFormatterParams<any, any>) => {
    if (!node) {
      return null;
    }
    const value = node.data?.[column] ?? node.aggData?.[column];

    if (_.isNil(value)) {
      return t('VANTAGE.COLUMN_CONFIG.NO_DATA');
    } else if (_.isNumber(value)) {
      return formatNumber(value, { format: AUTO_FORMAT });
    } else {
      return value;
    }
  };
};

export const generateDateValueFormatter = (column: string, timeZone: string) => {
  return (params: ValueFormatterParams<any, any>) => {
    try {
      const data = params.data ?? params.node?.aggData;
      const value = data?.[column];
      if (value) {
        const date = isoToLocalTimeZoneDate(value, { name: timeZone });

        return isNaN(date.getTime?.()) ? '' : dateTimeFormatter(date);
      }

      return '';
    } catch (err) {
      console.error(`Error in accessing ${column}:`, err);
    }

    return '';
  };
};

export const durationValueFormatter = (params: ValueFormatterParams<any, any>) =>
  _.isNil(params.value) ? '' : durationNanosecondsToString(params.value);

// Cache Formatters (they're expensive to instantiate)
export const decimalFormatters = [0, 1, 2, 3, 4, 5, 6].map(
  (d) =>
    new Intl.NumberFormat(undefined, {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }),
);

export const getNumericValueFormatter =
  (
    // The numericColumns will be populated after column definitions are
    // created, so we need to hold the reference so the value formatter can get
    // the decimals later.
    numericColumns: React.RefObject<Record<string, number>>,
    columnName: string,
  ) =>
  (params: ValueFormatterParams<any, any>) => {
    const decimals = numericColumns.current[columnName] ?? 0;
    const val = params.value;
    // Handle the string '0' case from possiblyZeroValueGetter
    if (val === '0') {
      return decimalFormatters[decimals]!.format(0);
    }
    if (val === null || typeof val !== 'number' || isNaN(val) || !isFinite(val)) {
      return '';
    }
    return decimalFormatters[decimals]!.format(val);
  };
